import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import DOMPurify from "dompurify";
import prevencherChatStyles from "./chat.module.css";
import Message from "../message/message";
import {
  allowedTypes,
  delay,
  findNextUnansweredQuestion,
  formatDateTimeInIsrael,
  isValidInternationalPhone,
  maxFileSizeMB,
  normalizeDashes,
  prevencherId,
} from "../../utils/utils";
import { useAppSelector } from "../../services/hooks";
import {
  messagesSelector,
  currentQuestionSelector,
  optionsSelector,
  currentQuestionIdSelector,
  allAnswersSelector,
  currentQuestionnaireSelector,
  errorSelector,
  allQuestionsSelector,
  questionnaireCompaniesIdSelector,
  allAudioAnswersSelector,
  allFileAnswersSelector,
  fakeCompanyIdSelector,
  audioUrlsForConstQuestionnaireSelector,
} from "../../services/selectors";
import { useActions } from "../../services/hooks/useActions";
import Loader from "../loader/loader";
import {
  useLazyGetUserByPhoneNumberQuery,
  useInsertAllAnswersQuestionsMutation,
  useLazyGetCurrentQuestionnaireByNameQuery,
  useLazyGetQuestionByIdQuery,
  useLazyGetCurrentQuestionnaireByGuidQuery,
  useLazyGetQuestionnaireByComponentQuery,
  useInsertAudioAnswersQuestionsMutation,
  usePostFilesMutation,
} from "../../services/api/api";
import {
  IVerificationData,
  TQuestionOption,
} from "../../services/types";
import { TGetUserByPhoneUserPayload } from "../../types/types";
import { useParams } from "react-router-dom";
import Modal from "../modal/Modal";
import UserExistPopup from "../userExistPopup/userExistPopup";
import PasswordVerificationPopup from "../passwordVerificationPopup/passwordVerificationPopup";
import PhoneExistsPopup from "../phoneExistsPopup/phoneExistsPopup";
import VoiceMessageButton from "../voiceMessageButton/voiceMessageButton";
import FileUpload from "../fileUpload/fileUpload";
import FilePreview from "../FilePreview/FilePreview";
import SignaturePad from "../signaturePad/signaturePad";
import { tWithKeyAttr as t } from "../../i18nHelper";
import ProgressBar from "../progressLine/progressLine";
import {
  useGetAudioUrlsFromTextsMutation,
  useGetResponseFromAIMutation,
  useGetTranscriptionMutation,
} from "../../services/api/ai.api";
import { IMessage } from "../../utils/types";
import ReplyBar from "../replyBar/replyBar";
import { useVoiceRecorder } from "../../hooks/useVoiceRecorder";
import VenchySpeaking from "../venchySpeaking/venchySpeaking";
import i18n from "../../i18n";
import { DataTypes } from "../../utils/constants";

function Chat({ u }: { u: null | string }) {
  const { name, symbol } = useParams();
  const searchParams = new URLSearchParams(window.location.search);

  const therapistGuid = searchParams.get("therapistGuid");
  // "u" is the short form a protocol's link uses; "userGuid" is the long one every other survey
  // link in the product uses, and the one already sitting in patients' text messages. Both are
  // read so a link that was sent before the shortening still opens.
  const userGuid = searchParams.get("u") ?? searchParams.get("userGuid");
  const questionnaireCompaniesTypeId = searchParams.get(
    "questionnaireCompaniesTypeId"
  );
  // A treatment protocol names the component outright rather than a type, and says which session
  // the answers belong to. Both are absent on the ordinary survey links.
  const componentIdParam =
    searchParams.get("q") ?? searchParams.get("questionnaireCompaniesId");
  const appointmentId = searchParams.get("a") ?? searchParams.get("appointmentId");


  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const replyBarRef = useRef<HTMLTextAreaElement>(null);
  const lastElRef = useRef<HTMLDivElement | null>(null);

  const [getUserByPhoneNumber] = useLazyGetUserByPhoneNumberQuery();
  const [
    getCurrentQuestionnaireByName,
    { isLoading: isQuestionnaireLoading },
  ] = useLazyGetCurrentQuestionnaireByNameQuery();
  const [
    getCurrentQuestionnaireByGuid,
    { isLoading: isQuestionnaireGuidLoading },
  ] = useLazyGetCurrentQuestionnaireByGuidQuery();
  const [
    getQuestionnaireByComponent,
    { isLoading: isQuestionnaireComponentLoading },
  ] = useLazyGetQuestionnaireByComponentQuery();
  const [insertAllAudioAnswers] = useInsertAudioAnswersQuestionsMutation();
  const [postFiles] = usePostFilesMutation();
  const [insertAllAnswers] = useInsertAllAnswersQuestionsMutation();
  const [getQuestionById] = useLazyGetQuestionByIdQuery();
  const [sendMessageToAI, { isLoading: isAiResponding }] =
    useGetResponseFromAIMutation();
  const [getAudio] = useGetAudioUrlsFromTextsMutation();

  const user = { id: "guest" };
  const messages = useAppSelector(messagesSelector);
  const options = useAppSelector(optionsSelector);
  const currentQuestionId = useAppSelector(currentQuestionIdSelector);
  const currentQuestion = useAppSelector(currentQuestionSelector);
  const currentQuestionnaire = useAppSelector(currentQuestionnaireSelector);
  const loadError = useAppSelector(errorSelector);
  const fakeCompanyId = useAppSelector(fakeCompanyIdSelector);
  const questionnaireCompaniesId = useAppSelector(
    questionnaireCompaniesIdSelector
  );
  const allAnswers = useAppSelector(allAnswersSelector);
  const allAudioAnswers = useAppSelector(allAudioAnswersSelector);
  const allFileAnswers = useAppSelector(allFileAnswersSelector);
  const allQuestions = useAppSelector(allQuestionsSelector);
  const audioUrls = useAppSelector(audioUrlsForConstQuestionnaireSelector);
  const totalQuestions = useMemo(() => {
    if (!allQuestions || !currentQuestionnaire) {
      return 0;
    }

    const regularQuestionsCount = allQuestions.filter(
      (q) => q.DataTypesId !== DataTypes.AI && q.DataTypesId !== 5 // לא AI ולא מעבר אוטומטי
    ).length;

    const aiSessions = allQuestions.filter(
      (q) => q.DataTypesId === DataTypes.AI
    );

    const aiQuestionsCount =
      aiSessions.length * (currentQuestionnaire.AIQuestionsLimit ?? 0);

    return regularQuestionsCount + aiQuestionsCount;
  }, [allQuestions, currentQuestionnaire]);
  const [answeredCount, setAnsweredCount] = useState(0);
  const {
    setMessages,
    updateMessage,
    setOptions,
    setAnswers,
    setAudioAnswers,
    setFileAnswers,
  } = useActions();

  const [isLoading, setIsLoading] = useState(false);
  const [isUserExist, setUserExist] = useState(false);
  const [UserGuid, setUserGuid] = useState<string | null>(null);
  const [showInput, setShowInput] = useState(false);
  const [showTextArea, setShowTextArea] = useState(false);
  const [userExistPopup, setUserExistPopup] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [passwordVerifecationPopup, setPasswordVerifecationPopup] =
    useState<boolean>(false);
  const [currentPhone, setCurrentPhone] = useState<string | null>(null);
  const [verificationData, setVerificationData] =
    useState<IVerificationData | null>(null);
  const [usersId, setUsersId] = useState<number | null>(null);
  const [showVoiceMessageButton, setShowVoiceMessageButton] = useState(false);
  const [showFileUpload, setShowFileUpload] = useState(false);
  const [showSignature, setShowSignature] = useState(false);
  const [isEnd, setIsEnd] = useState<boolean>(false);
  const [filePreviewUrls, setFilePreviewUrls] = useState<string[]>([]);
  const [tempFiles, setTempFiles] = useState<File[]>([]);
  const [isAIPhase, setIsAIPhase] = React.useState(false);
  const [aiQuestionsRemaining, setAiQuestionsRemaining] = React.useState(0);
  const [nextStructuredQuestionId, setNextStructuredQuestionId] = useState<
    number | null
  >(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [getTranscription] = useGetTranscriptionMutation();
  const [showReplyBarForStructured, setShowReplyBarForStructured] =
    useState(false);
  const { isRecording, audioURL, startRecording, stopRecording, deleteRecord } =
    useVoiceRecorder();
  const [irrelevantAnswerCount, setIrrelevantAnswerCount] = useState(0);

  // Multi-select state
  const [selectedOptions, setSelectedOptions] = useState<number[]>([]);
  const [validationError, setValidationError] = useState<string>("");

  // Phone exists popup state
  const [phoneExistsPopup, setPhoneExistsPopup] = useState(false);
  const [pendingPhoneData, setPendingPhoneData] = useState<{
    guid: string;
    isFinished: boolean;
    originalPhone: string;
  } | null>(null);

  const lastMessageAudio = messages[messages.length - 1]?.audio;
  const currentAudio = audioUrls?.find(
    (a) => a.questionId.toString() === messages[messages.length - 1]?.messageId
  );

  //for existing users
  useEffect(() => {
    if (userGuid && componentIdParam) {
      // Sent by a treatment protocol: one named questionnaire, for one session.
      getQuestionnaireByComponent({
        guid: userGuid,
        questionnaireCompaniesId: componentIdParam,
        appointmentId,

      });
    } else if (userGuid && questionnaireCompaniesTypeId) {
      getCurrentQuestionnaireByGuid({
        guid: userGuid,
        questionnaireCompaniesTypeId: questionnaireCompaniesTypeId,
        isForTHerapist: false,
      });
    } else if (therapistGuid && questionnaireCompaniesTypeId) {
      getCurrentQuestionnaireByGuid({
        guid: therapistGuid,
        questionnaireCompaniesTypeId: questionnaireCompaniesTypeId,
        isForTHerapist: false,
      });
    } else {
      getCurrentQuestionnaireByName({ name, symbol });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    getCurrentQuestionnaireByName,
    name,
    userGuid,
    symbol,
    componentIdParam,
    appointmentId,
  ]);



  useEffect(() => {
    if (isEnd) {
      insertAllAnswers({
        answers: allAnswers,
        companyId: currentQuestionnaire?.CompanyId ?? 0,
        questionnaireCompaniesId: questionnaireCompaniesId,
        therapistGuid: therapistGuid ?? null,
        usersGuid: userGuid ?? null,
        fakeCompanyId: fakeCompanyId ?? null,
        appointmentId,
      }).then((res) => {
        if ("data" in res) {
          allAudioAnswers.length > 0 &&
            insertAllAudioAnswers({
              answers: allAudioAnswers,
              companyId: currentQuestionnaire?.CompanyId ?? 0,
              questionnaireCompaniesScoreUsersId:
                res.data.questionnaireCompaniesScoreUsersId,
              guid: userGuid ?? res.data.userguid,
            });

          if (allFileAnswers.length > 0) {
            postFiles({
              files: allFileAnswers,
              questionnaireCompaniesScoreUsersId:
                res.data.questionnaireCompaniesScoreUsersId,
              userId: userGuid ?? res.data.userguid,
            });
          }
        }
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnd]);

  //show start question and bring first question
  useEffect(
    () => {
      const nextQId = allQuestions && allQuestions[0]?.Id;
      if (!currentQuestionnaire || !nextQId) return;

      currentQuestionnaire?.StartQuestion &&
        setMessages({
          messageId: "start",
          id: prevencherId,
          date: new Date(),
          message: currentQuestionnaire.StartQuestion,
          nextQuestionId: nextQId,
          questionId: currentQuestionId,
          // audio:צריך להוסיף לשאלון שיחזור לשאלה ראשונה אודיו
        });

      setIsLoading(true);

      delay(1500).then(() => {
        getQuestionById({
          nextQuestionId: u && currentQuestionnaire?.Id === 26 ? 76 : nextQId,
          skip: false,
          questionnaireId: currentQuestionnaire?.Id,
        })
          .then((res) => {
            if ("data" in res && res.data && res.data.Questions) {
              const question = res.data.Questions;

              if (currentQuestionnaire?.VenchyEnabled) {
                setIsLoading(true);
                getAudio([{ id: question.Id, desc: question.Desc }]).finally(
                  () => {
                    setIsLoading(false);
                  }
                );
              } else {
                setIsLoading(false);
              }
            } else {
              setIsLoading(false);
            }
          })
          .catch((err) => {
            console.error(t("ERROR_IN_RECEIVING_FIRST_QUESTION"), err);
            setIsLoading(false);
          });
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentQuestionnaire, getQuestionById, setMessages]
  );

  // useEffect(() => {
  //   const nextQId = allQuestions && allQuestions[0]?.Id;
  //   if (currentQuestionnaire && nextQId) {
  //     currentQuestionnaire?.StartQuestion &&
  //       setMessages({
  //         messageId: "start",
  //         id: prevencherId,
  //         date: new Date(),
  //         message: currentQuestionnaire.StartQuestion,
  //         nextQuestionId: nextQId,
  //         questionId: currentQuestionId,
  //       });
  //     setIsLoading(true);
  //     nextQId &&
  //       delay(1500).then(() => {
  //         if (isAIPhase) {
  //           const initialAiMessage = `הי ונצי`;
  //           sendMessageToAI({
  //             message: initialAiMessage,
  //             usersId: user?.id || "1",
  //             conversationId: currentQuestionnaire.ChatGPTFeaturesId,
  //           });
  //         } else {
  //           getQuestionById({
  //             nextQuestionId:
  //               u && currentQuestionnaire?.Id === 26 ? 76 : nextQId,
  //             skip: false,
  //             questionnaireId: currentQuestionnaire?.Id,
  //           }).then(() => setIsLoading(false));
  //         }
  //       });
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [currentQuestionnaire, getQuestionById, setMessages, isAIPhase]);

  const scrollToBottom = () => {
    lastElRef.current?.scrollIntoView({
      behavior: "smooth",
    });
  };
  useEffect(() => {
    scrollToBottom();
  });

  //autofocus
  useEffect(() => {
    if (messages.length === 0) return;

    const lastMessage = messages[messages.length - 1];

    const isLastMessageFromBot =
      lastMessage.id === "ai-assistant" || lastMessage.id === prevencherId;

    if (isLastMessageFromBot) {
      if (isAIPhase || showReplyBarForStructured) {
        setTimeout(() => {
          replyBarRef.current?.focus();
        }, 100);
      } else if (showInput || showTextArea) {
        setTimeout(() => {
          textAreaRef.current?.focus();
        }, 100);
      }
    }
  }, [messages, showInput, showTextArea, isAIPhase, showReplyBarForStructured]);

  //open input according to dataTypes of current question, need to think when we open text input or textarea
  useEffect(() => {
    if (!currentQuestion) return;

    // if (!currentQuestion.NextQuestionId) {
    //   setIsEnd(true);
    // }

    if (!isAIPhase && !currentQuestion.Id) {
      return;
    }

    const { DataTypesId, NextQuestionId } = currentQuestion;

    setShowInput(false);
    setShowTextArea(false);
    setShowVoiceMessageButton(false);
    setShowFileUpload(false);
    setShowSignature(false);
    setShowReplyBarForStructured(false);
    setSelectedOptions([]);
    setValidationError("");
    DataTypesId !== 1 && setOptions([]);

    switch (DataTypesId) {
      case DataTypes.Text:
      case DataTypes.Voice:
        if (currentQuestion.UserTableField === "Phone" || currentQuestion.UserTableField === "FirstName") {
          setShowInput(true);
        } else {
          setShowReplyBarForStructured(true);
        }
        break;

      case DataTypes.Number:
      case DataTypes.Date:
        setShowInput(true);
        break;

      case DataTypes.AutoAdvance:
        const advanceAutomatically = async () => {
          setIsLoading(true);
          try {
            await delay(1500);
            if (NextQuestionId && currentQuestionnaire?.Id) {
              const res = await getQuestionById({
                nextQuestionId: NextQuestionId,
                skip: false,
                questionnaireId: currentQuestionnaire.Id,
              });

              if (
                "data" in res &&
                res.data &&
                res.data.Questions &&
                currentQuestionnaire?.VenchyEnabled
              ) {
                const question = res.data.Questions;
                await getAudio([{ id: question.Id, desc: question.Desc }]);
              }
            } else {
              setIsEnd(true);
            }
          } catch (err) {
            console.error("Error during auto-advance:", err);
          } finally {
            setIsLoading(false);
          }
        };

        advanceAutomatically();
        break;

      case DataTypes.File:
        setShowFileUpload(true);
        break;

      case DataTypes.Signature:
        setShowSignature(true);
        break;

      case DataTypes.AI:
        setIsAIPhase(true);
        setAiQuestionsRemaining(currentQuestionnaire?.AIQuestionsLimit ?? 5); // 5 as default
        setNextStructuredQuestionId(NextQuestionId);
        setIrrelevantAnswerCount(0);

        if (currentQuestion.Question) {
          const initialAiMessage: IMessage = {
            messageId: `ai-starter-${currentQuestion.Id}`,
            id: "ai-assistant",
            date: new Date().toISOString(),
            message: currentQuestion.Question,
          };
          setMessages(initialAiMessage);
        }
        break;
      default:
        // do nothing, everything is hidden
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQuestion, currentQuestionnaire?.Id, getQuestionById, setOptions]);

  const handleAnswer = useCallback(() => {
    setAnsweredCount((prev) => Math.min(prev + 1, totalQuestions));
  }, [setAnsweredCount, totalQuestions]);

  const isMultiSelect = useMemo(() => {
    return (currentQuestion?.OptionsCountMax ?? 0) > 1;
  }, [currentQuestion?.OptionsCountMax]);

  const canSkipQuestion = useMemo(() => {
    // Don't allow skip if UserTableField is set (required fields like Phone, FirstName, etc.)
    if (currentQuestion?.UserTableField) {
      return false;
    }
    return (currentQuestion?.OptionsCountMin ?? 0) < 1;
  }, [currentQuestion?.OptionsCountMin, currentQuestion?.UserTableField]);

  const handleSkipQuestion = useCallback(async () => {
    if (!currentQuestion || !currentQuestionnaire) return;

    // Increment progress bar for skipped question
    handleAnswer();

    setIsLoading(true);
    setShowInput(false);
    setShowTextArea(false);
    setShowReplyBarForStructured(false);
    setShowVoiceMessageButton(false);
    setShowFileUpload(false);
    setOptions([]);
    setSelectedOptions([]);
    setValidationError("");

    // Add a message indicating the question was skipped
    setMessages({
      messageId: `skip-${currentQuestionId}`,
      id: user.id,
      date: formatDateTimeInIsrael(new Date()),
      unread: false,
      message: t("SKIP_QUESTION"),
      questionId: currentQuestionId,
    });

    try {
      await delay(1500);

      const nextQId = currentQuestion.NextQuestionId;
      if (nextQId && currentQuestionnaire.Id) {
        const res = await getQuestionById({
          nextQuestionId: nextQId,
          skip: false,
          questionnaireId: currentQuestionnaire.Id,
        });

        if (
          "data" in res &&
          res.data &&
          res.data.Questions &&
          currentQuestionnaire?.VenchyEnabled
        ) {
          const question = res.data.Questions;
          await getAudio([{ id: question.Id, desc: question.Desc }]);
        }
      } else {
        setIsEnd(true);
      }
    } catch (err) {
      console.error("Error skipping question:", err);
    } finally {
      setIsLoading(false);
    }
  }, [
    currentQuestion,
    currentQuestionId,
    currentQuestionnaire,
    user.id,
    setMessages,
    getQuestionById,
    getAudio,
    setOptions,
    handleAnswer,
  ]);

  const handleOptionToggle = (option: TQuestionOption) => {
    if (!isMultiSelect) {
      onOptionClick(option);
      return;
    }

    setValidationError("");
    setSelectedOptions((prev) => {
      if (prev.includes(option.Id)) {
        return prev.filter((id) => id !== option.Id);
      } else {
        const maxCount = currentQuestion?.OptionsCountMax ?? 1;
        if (prev.length >= maxCount) {
          return prev;
        }
        return [...prev, option.Id];
      }
    });
  };

  const handleMultiSelectSubmit = () => {
    const minCount = currentQuestion?.OptionsCountMin ?? 0;
    const maxCount = currentQuestion?.OptionsCountMax ?? 1;

    // Allow submission with 0 selections if OptionsCountMin < 1
    if (selectedOptions.length === 0 && canSkipQuestion) {
      handleSkipQuestion();
      return;
    }

    if (selectedOptions.length < minCount) {
      setValidationError(
        t("PLEASE_SELECT_AT_LEAST_OPTIONS", { count: minCount })
      );
      return;
    }

    if (selectedOptions.length > maxCount) {
      setValidationError(
        t("PLEASE_SELECT_NO_MORE_THAN_OPTIONS", { count: maxCount })
      );
      return;
    }

    setValidationError("");
    setOptions([]);
    setIsLoading(true);

    const selectedOptionsData = options.filter((opt) =>
      selectedOptions.includes(opt.Id)
    );
    const optionDescriptions = selectedOptionsData
      .map((opt) => opt.Desc)
      .join(", ");
    const optionIds = selectedOptions.join(",");

    setMessages({
      messageId: `o${optionIds}`,
      id: user.id,
      date: formatDateTimeInIsrael(new Date()),
      unread: false,
      message: optionDescriptions,
      nextQuestionId: currentQuestion?.NextQuestionId,
      questionId: currentQuestionId,
    });

    setAnswers({
      Id: "0",
      QuestionnaireId: currentQuestionnaire?.Id.toString(),
      QuestionsId: currentQuestionId.toString(),
      UsersId: usersId,
      Answer: null,
      QuestionsOptionsIds: optionIds,
    });

    setSelectedOptions([]);
    handleAnswer();

    delay(1500).then(() => {
      if (currentQuestionnaire?.Id) {
        // First try to use the question's NextQuestionId
        let nextQId = currentQuestion?.NextQuestionId;
        
        // If question's NextQuestionId is null, find first non-null NextQuestionId from selected options
        if (!nextQId) {
          const firstOptionWithNext = selectedOptionsData.find(
            (opt) => opt.NextQuestionId
          );
          nextQId = firstOptionWithNext?.NextQuestionId;
        }
        
        if (nextQId) {
          setIsLoading(true);
          getQuestionById({
            nextQuestionId: nextQId,
            questionnaireId: currentQuestionnaire.Id,
          })
            .then((res) => {
              if ("data" in res && res.data && res.data.Questions) {
                const question = res.data.Questions;
                if (currentQuestionnaire?.VenchyEnabled) {
                  getAudio([{ id: question.Id, desc: question.Desc }]).finally(
                    () => {
                      setIsLoading(false);
                    }
                  );
                } else {
                  setIsLoading(false);
                }
              } else {
                setIsLoading(false);
              }
            })
            .catch((err) => {
              console.error(t("ERROR_IN_RECEIVING_NEXT_QUESTION"), err);
              setIsLoading(false);
            });
        } else {
          setIsLoading(false);
          setIsEnd(true);
        }
      } else {
        setIsLoading(false);
      }
    });
  };

  const onOptionClick = (option: TQuestionOption) => {
    setOptions([]);
    setIsLoading(true);
    setMessages({
      messageId: `o${option.Id}`,
      id: user.id,
      date: formatDateTimeInIsrael(new Date()),
      unread: false,
      message: option.Desc,
      nextQuestionId: option.NextQuestionId,
      questionId: currentQuestionId,
    });
    setAnswers({
      Id: "0",
      QuestionnaireId: currentQuestionnaire?.Id.toString(),
      QuestionsId: currentQuestionId.toString(),
      UsersId: usersId,
      Answer: null,
      QuestionsOptionsIds: option.Id.toString(),
      // ...(id ? { Cycle: 1 } : {}), // Only add Cycle if id exists
    });
    delay(1500).then(() => {
      //not in use now
      // const nextQuestion =
      //   allQuestions &&
      //   findNextUnansweredQuestion(allQuestions, option.NextQuestionId);
      if (currentQuestionnaire?.Id) {
        const nextQId =
          option.NextQuestionId || currentQuestion?.NextQuestionId;
        if (nextQId) {
          setIsLoading(true);
          getQuestionById({
            nextQuestionId: u && option.NextQuestionId === 23 ? 25 : nextQId,
            questionnaireId: currentQuestionnaire.Id,
          })
            .then((res) => {
              if ("data" in res && res.data && res.data.Questions) {
                const question = res.data.Questions;
                if (currentQuestionnaire?.VenchyEnabled) {
                  getAudio([{ id: question.Id, desc: question.Desc }]).finally(
                    () => {
                      setIsLoading(false);
                    }
                  );
                } else {
                  setIsLoading(false);
                }
              } else {
                setIsLoading(false);
              }
            })
            .catch((err) => {
              console.error(t("ERROR_IN_RECEIVING_NEXT_QUESTION"), err);
              setIsLoading(false);
            });
        } else {
          setIsLoading(false);
          setIsEnd(true);
        }
      } else {
        setIsLoading(false);
      }
    });
  };

  const checkPhoneExist = useCallback(
    async (inputValue: string): Promise<{ userExists: boolean; shouldShowPhoneExistsPopup: boolean; shouldShowVerificationPopup: boolean }> => {
      const res = await getUserByPhoneNumber({
        phone: inputValue,
        companyId: currentQuestionnaire?.CompanyId ?? -1,
        questionnaireId: currentQuestionnaire?.Id,
      });

      const data = await res.data;

      // Check if user exists by looking for user-specific properties
      // When user doesn't exist: { success, message, type, smsCount, isMail: false/true }
      // When user exists: { id, guid, phone, firstName, ... }
      const isUserExists = data && "guid" in data;

      if (isUserExists) {
        // User exists - store data and show phone exists popup
        const payload = data as TGetUserByPhoneUserPayload;
        setPendingPhoneData({
          guid: payload.guid,
          isFinished: payload.IsFinished === true,
          originalPhone: inputValue,
        });
        setPhoneExistsPopup(true);
        return { userExists: true, shouldShowPhoneExistsPopup: true, shouldShowVerificationPopup: false };
      } else {
        // User doesn't exist - show verification popup
        // data contains { success, message, isMail, ... }
        if (data && "isMail" in data) {
          setVerificationData(data as IVerificationData);
          setCurrentPhone(inputValue);
          setPasswordVerifecationPopup(true);
          return { userExists: false, shouldShowPhoneExistsPopup: false, shouldShowVerificationPopup: true };
        }
        // Fallback - continue normally (shouldn't happen)
        return { userExists: false, shouldShowPhoneExistsPopup: false, shouldShowVerificationPopup: false };
      }
    },
    [
      getUserByPhoneNumber,
      currentQuestionnaire?.Id,
    ]
  );

  const handleContinueWithExistingPhone = useCallback(() => {
    setPhoneExistsPopup(false);

    if (pendingPhoneData) {
      // User exists - show user exist popup
      setUserExist(true);
      setUserExistPopup(true);
      if (pendingPhoneData.isFinished) {
        setUserGuid(pendingPhoneData.guid);
      }
    }

    setPendingPhoneData(null);
  }, [pendingPhoneData]);

  const handleNewPhoneSubmit = useCallback(
    
    async (newPhone: string) => {
      setPhoneExistsPopup(false);
      setPendingPhoneData(null);

      // Replace the previous phone message with the new phone number
      updateMessage({
        questionId: currentQuestionId,
        newMessage: newPhone,
      });

      // Update the answer with the new phone number (replaces old answer)
      if (currentQuestionnaire?.Id) {
        setAnswers({
          Id: "0",
          QuestionnaireId: currentQuestionnaire.Id.toString(),
          QuestionsId: currentQuestionId.toString(),
          UsersId: usersId ?? "0",
          Answer: newPhone,
        });
      }

      // Check if new phone exists
      const result = await checkPhoneExist(newPhone);

      if (result.shouldShowPhoneExistsPopup || result.shouldShowVerificationPopup) {
        // A popup was shown (either phone exists or verification needed)
        // Message and answer already updated, wait for user interaction
        setShowInput(false);
        setShowTextArea(false);
        return;
      }

      // No popup shown - continue with the questionnaire (shouldn't normally happen)
      setShowInput(false);
      setInputValue("");
      setShowTextArea(false);
      setShowReplyBarForStructured(false);

      setIsLoading(true);
      try {
        await delay(1500);

        if (currentQuestion?.NextQuestionId && currentQuestionnaire) {
          const res = await getQuestionById({
            nextQuestionId: currentQuestion.NextQuestionId,
            skip: false,
            questionnaireId: currentQuestionnaire.Id,
          });

          if (
            "data" in res &&
            res.data &&
            res.data.Questions &&
            currentQuestionnaire?.VenchyEnabled
          ) {
            const question = res.data.Questions;
            await getAudio([{ id: question.Id, desc: question.Desc }]);
          }
        } else {
          setIsEnd(true);
        }
      } catch (err) {
        console.error("Error submitting input and getting next question:", err);
      } finally {
        setIsLoading(false);
      }
    },
    [
      checkPhoneExist,
      currentQuestion,
      currentQuestionId,
      currentQuestionnaire,
      usersId,
      updateMessage,
      setAnswers,
      getQuestionById,
      getAudio,
    ]
  );

  const handleContinueQuestionnaireAfterUserExist = useCallback(async () => {
    setUserExistPopup(false);
    setUserExist(false);

    // Set the answer with the current phone (from the message)
    if (currentQuestionnaire?.Id) {
      const phoneMessage = messages.find(
        (msg) => msg.questionId === currentQuestionId && msg.id === user.id
      );
      if (phoneMessage) {
        setAnswers({
          Id: "0",
          QuestionnaireId: currentQuestionnaire.Id.toString(),
          QuestionsId: currentQuestionId.toString(),
          UsersId: usersId ?? "0",
          Answer: phoneMessage.message,
        });
      }
    }

    // Continue to the next question
    setIsLoading(true);
    try {
      await delay(1500);

      if (currentQuestion?.NextQuestionId && currentQuestionnaire) {
        const res = await getQuestionById({
          nextQuestionId: currentQuestion.NextQuestionId,
          skip: false,
          questionnaireId: currentQuestionnaire.Id,
        });

        if (
          "data" in res &&
          res.data &&
          res.data.Questions &&
          currentQuestionnaire?.VenchyEnabled
        ) {
          const question = res.data.Questions;
          await getAudio([{ id: question.Id, desc: question.Desc }]);
        }
      } else {
        setIsEnd(true);
      }
    } catch (err) {
      console.error("Error continuing questionnaire:", err);
    } finally {
      setIsLoading(false);
    }
  }, [
    currentQuestion,
    currentQuestionId,
    currentQuestionnaire,
    messages,
    user.id,
    usersId,
    setAnswers,
    getQuestionById,
    getAudio,
  ]);

  const onInputSubmit = useCallback(
    async (textToSubmit: string) => {
      if (!textToSubmit.trim()) return;

      handleAnswer();

      if (currentQuestion?.UserTableField === "Phone") {
        if (!isValidInternationalPhone(textToSubmit)) {
          setMessages({
            messageId: `${textToSubmit}error`,
            id: prevencherId,
            date: formatDateTimeInIsrael(new Date()),
            unread: false,
            message: t("INVALID_MOBILE_NUMBER", { textToSubmit }),
            nextQuestionId: currentQuestion?.NextQuestionId,
            questionId: currentQuestionId,
          });
          return;
        }
        if (!therapistGuid) {
          const result = await checkPhoneExist(textToSubmit);
          if (result.shouldShowPhoneExistsPopup || result.shouldShowVerificationPopup) {
            // A popup was shown - wait for user decision
            setShowInput(false);
            setShowTextArea(false);
            setMessages({
              messageId: `${currentQuestionId}${textToSubmit}`,
              id: user.id,
              date: formatDateTimeInIsrael(new Date()),
              unread: false,
              message: textToSubmit,
              nextQuestionId: currentQuestion?.NextQuestionId,
              questionId: currentQuestionId,
            });
            return;
          }
        }
      }

      setShowInput(false);
      setInputValue("");
      setShowTextArea(false);
      setShowReplyBarForStructured(false);
      setMessages({
        messageId: `${currentQuestionId}${textToSubmit}`,
        id: user.id,
        date: formatDateTimeInIsrael(new Date()),
        unread: false,
        message: textToSubmit,
        questionId: currentQuestionId,
      });

      if (currentQuestionnaire?.Id) {
        setAnswers({
          Id: "0",
          QuestionnaireId: currentQuestionnaire.Id.toString(),
          QuestionsId: currentQuestionId.toString(),
          UsersId: usersId ?? "0",
          Answer: textToSubmit,
        });
      }

      setIsLoading(true);
      try {
        await delay(1500);

        if (currentQuestion?.NextQuestionId && currentQuestionnaire) {
          const res = await getQuestionById({
            nextQuestionId: currentQuestion.NextQuestionId,
            skip: false,
            questionnaireId: currentQuestionnaire.Id,
          });

          if (
            "data" in res &&
            res.data &&
            res.data.Questions &&
            currentQuestionnaire?.VenchyEnabled
          ) {
            const question = res.data.Questions;
            await getAudio([{ id: question.Id, desc: question.Desc }]);
          }
        } else {
          setIsEnd(true);
        }
      } catch (err) {
        console.error("Error submitting input and getting next question:", err);
      } finally {
        setIsLoading(false);
      }
    },
    [
      currentQuestion,
      currentQuestionId,
      currentQuestionnaire,
      therapistGuid,
      checkPhoneExist,
      user.id,
      usersId,
      setMessages,
      setCurrentPhone,
      setPasswordVerifecationPopup,
      setShowInput,
      setShowTextArea,
      setShowReplyBarForStructured,
      setIsLoading,
      setAnswers,
      getQuestionById,
      setIsEnd,
      setInputValue,
      handleAnswer,
      getAudio,
    ]
  );

  const onSignatureSubmit = useCallback(
    async (signatureDataUrl: string) => {
      // Defense in depth: only accept a PNG data-URL as the answer value.
      if (!signatureDataUrl.startsWith("data:image/png;base64,")) return;

      handleAnswer();
      setShowSignature(false);

      setMessages({
        messageId: `${currentQuestionId}-signature-${Date.now()}`,
        id: user.id,
        date: formatDateTimeInIsrael(new Date()),
        unread: false,
        message: t("SIGNATURE_ADDED"),
        questionId: currentQuestionId,
      });

      if (currentQuestionnaire?.Id) {
        setAnswers({
          Id: "0",
          QuestionnaireId: currentQuestionnaire.Id.toString(),
          QuestionsId: currentQuestionId.toString(),
          UsersId: usersId ?? "0",
          Answer: signatureDataUrl,
        });
      }

      setIsLoading(true);
      try {
        await delay(1500);

        if (currentQuestion?.NextQuestionId && currentQuestionnaire) {
          const res = await getQuestionById({
            nextQuestionId: currentQuestion.NextQuestionId,
            skip: false,
            questionnaireId: currentQuestionnaire.Id,
          });

          if (
            "data" in res &&
            res.data &&
            res.data.Questions &&
            currentQuestionnaire?.VenchyEnabled
          ) {
            const question = res.data.Questions;
            await getAudio([{ id: question.Id, desc: question.Desc }]);
          }
        } else {
          setIsEnd(true);
        }
      } catch (err) {
        console.error("Error submitting signature and getting next question:", err);
      } finally {
        setIsLoading(false);
      }
    },
    [
      currentQuestion,
      currentQuestionId,
      currentQuestionnaire,
      user.id,
      usersId,
      setMessages,
      setAnswers,
      setShowSignature,
      setIsLoading,
      getQuestionById,
      setIsEnd,
      handleAnswer,
      getAudio,
    ]
  );

  const handleAISendMessage = useCallback(
    async (text: string, conversationHistory: IMessage[]) => {
      const content = text;
      if (!content || isAiResponding) return;

      const lastAiMessage = [...conversationHistory]
        .reverse()
        .find((m) => m.id === "ai-assistant");
      if (currentQuestionnaire?.Id && lastAiMessage) {
        setAnswers({
          Id: "0",
          QuestionnaireId: currentQuestionnaire.Id.toString(),
          QuestionsId: "0",
          UsersId: usersId ?? "0",
          Answer: content,
          QuestionText: lastAiMessage.message,
          AnswerDate: new Date(),
          QuestionsOptionsIds: "",
        });
      }

      if (isAIPhase) {
        const isPotentiallyLastMessage = aiQuestionsRemaining <= 1;
        if (!isPotentiallyLastMessage) {
          if (currentQuestionnaire?.ChatGPTFeaturesId) {
            const conversationHistoryForAPI = conversationHistory.map(
              (msg) => ({
                Role: msg.id === user.id ? "user" : "assistant",
                Content: msg.message,
              })
            );

            try {
              const response = await sendMessageToAI({
                message: content,
                ConversationHistory: conversationHistoryForAPI,
                isAudio: true,
                conversationId: currentQuestionnaire.ChatGPTFeaturesId,
                irrelevantCount: irrelevantAnswerCount,
              }).unwrap();

              if (response.reply) {
                const aiResponse: IMessage = {
                  messageId: `ai-${Date.now()}`,
                  id: "ai-assistant",
                  date: new Date().toISOString(),
                  message: normalizeDashes(response.reply),
                  audio: response.audio,
                };
                setMessages(aiResponse);
              }

              if (response.newIrrelevantCount !== undefined) {
                setIrrelevantAnswerCount(response.newIrrelevantCount);
              }

              if (response.shouldDecrementLimit) {
                setAiQuestionsRemaining((prev) => prev - 1);
                handleAnswer();
              }
            } catch (err) {
              console.error("Error in AI conversation", err);
              const errorMessage: IMessage = {
                messageId: `error-${Date.now()}`,
                id: "ai-assistant",
                date: new Date().toISOString(),
                message: t("OOPS_ERROR_TRY_AGAIN"),
              };
              setMessages(errorMessage);
            }
          }
          return;
        }

        if (isPotentiallyLastMessage) {
          handleAnswer();
          setIsLoading(true);
          setIsAIPhase(false);
          setAiQuestionsRemaining(0);

          if (nextStructuredQuestionId && currentQuestionnaire?.Id) {
            getQuestionById({
              nextQuestionId: nextStructuredQuestionId,
              skip: false,
              questionnaireId: currentQuestionnaire.Id,
            })
              .then((res) => {
                if ("data" in res && res.data && res.data.Questions) {
                  const question = res.data.Questions;
                  setIsLoading(true);
                  if (currentQuestionnaire?.VenchyEnabled) {
                    getAudio([
                      { id: question.Id, desc: question.Desc },
                    ]).finally(() => {
                      setIsLoading(false);
                    });
                  } else {
                    setIsLoading(false);
                  }
                } else {
                  setIsLoading(false);
                }
              })
              .catch((err) => {
                console.error(t("ERROR_RECEIVING_AUDIO"), err);
                setIsLoading(false);
              });
          } else {
            setIsLoading(false);
            setIsEnd(true);
          }
        }
      }
    },
    [
      irrelevantAnswerCount,
      isAiResponding,
      currentQuestionnaire,
      sendMessageToAI,
      setMessages,
      user.id,
      setAnswers,
      usersId,
      isAIPhase,
      aiQuestionsRemaining,
      nextStructuredQuestionId,
      getQuestionById,
      setIsEnd,
      handleAnswer,
      getAudio,
    ]
  );
  const handleAISendVoiceMessage = useCallback(
    async (audioBlob: Blob) => {
      setAudioAnswers({
        file: audioBlob,
        questionId: currentQuestion?.Id || 1,
      });
      const file = new File([audioBlob], "voice_message.webm", {
        type: audioBlob.type || "audio/webm",
        lastModified: Date.now(),
      });

      try {
        const transcriptionResponse = await getTranscription({ file }).unwrap();
        const recognizedText =
          transcriptionResponse.text || t("EMPTY_RECORDING");

        if (isAIPhase) {
          // handleAnswer();
          const userMessageWithTranscription: IMessage = {
            messageId: `user-voice-${Date.now()}`,
            id: user.id,
            date: new Date().toISOString(),
            message: recognizedText,
          };

          const updatedConversationHistory = [
            ...messages,
            userMessageWithTranscription,
          ];

          setMessages(userMessageWithTranscription);

          await handleAISendMessage(recognizedText, updatedConversationHistory);
        } else {
          await onInputSubmit(recognizedText);
        }
      } catch (error) {
        console.error("Error transcribing voice message:", error);
        const errorMessage: IMessage = {
          messageId: `error-${Date.now()}`,
          id: "ai-assistant",
          date: new Date().toISOString(),
          message: t("OOPS_ERROR_IN_TRANSCRIPTION"),
        };
        setMessages(errorMessage);
      }
    },
    [
      currentQuestion?.Id,
      setAudioAnswers,
      isAIPhase,
      messages,
      user.id,
      setMessages,
      onInputSubmit,
      getTranscription,
      handleAISendMessage,
    ]
  );

  const handleTextSendWrapper = (text: string) => {
    if (isAIPhase) {
      if (!text.trim()) return;
      // handleAnswer();

      const userMessage: IMessage = {
        messageId: `user-text-${Date.now()}`,
        id: user.id,
        date: new Date().toISOString(),
        message: text,
      };

      const updatedConversationHistory = [...messages, userMessage];
      setMessages(userMessage);

      handleAISendMessage(text, updatedConversationHistory);
    } else {
      onInputSubmit(text);
    }
  };

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    let sanitizedContent = DOMPurify.sanitize(e.target.value, {
      ALLOWED_TAGS: [], // Allow only certain tags, or use [] for none
      ALLOWED_ATTR: [], // Allow no attributes
    });
    if (textAreaRef.current) {
      textAreaRef.current.style.height = "30px";
      textAreaRef.current.style.height = `${textAreaRef.current.scrollHeight}px`;
    }
    currentQuestion?.UserTableField === "Phone"
      ? handleTelInputChange(e)
      : setInputValue(sanitizedContent);
  };
  const handleTelInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const inputValue = event.target.value;
    // Allow digits, dashes, parentheses, and leading plus; remove spaces entirely
    const cleaned = inputValue.replace(/[^0-9+()-]/g, "");
    // Enforce single leading plus if present
    const normalized = cleaned.replace(/(?!^)[+]/g, "");
    // Remove any remaining whitespace just in case
    const noSpaces = normalized.replace(/\s+/g, "");
    // Limit to 20 chars
    setInputValue(noSpaces.slice(0, 20));
  };
  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (event.key === "Enter") {
      onInputSubmit(inputValue);
    }
  };

  // handle audio answers
  const saveVoiceMessage = async (audioBlob: Blob): Promise<void> => {
    setShowVoiceMessageButton(false);
    const date = formatDateTimeInIsrael(new Date());
    setMessages({
      messageId: `${currentQuestionId}${inputValue}`,
      id: user.id,
      date: date,
      unread: false,
      message: t("VOICE_MESSAGE_SENT_SUCCESS"),
      nextQuestionId: currentQuestion?.NextQuestionId,
      questionId: currentQuestionId,
    });
    setIsLoading(true);
    setAudioAnswers({ file: audioBlob, questionId: currentQuestion?.Id || 1 });
    delay(1500).then(() => {
      setIsLoading(false);
      const nextQuestion =
        allQuestions &&
        currentQuestion?.NextQuestionId &&
        findNextUnansweredQuestion(
          allQuestions,
          currentQuestion.NextQuestionId
        );
      currentQuestion?.NextQuestionId &&
        currentQuestionnaire?.Id &&
        getQuestionById({
          nextQuestionId: nextQuestion
            ? nextQuestion.Id
            : currentQuestion.NextQuestionId,
          skip: false,
          questionnaireId: currentQuestionnaire.Id,
        })
          .then((res) => {
            if ("data" in res && res.data && res.data.Questions) {
              const question = res.data.Questions;
              setIsLoading(true);
              currentQuestionnaire?.VenchyEnabled &&
                getAudio([{ id: question.Id, desc: question.Desc }]).finally(
                  () => {
                    setIsLoading(false);
                  }
                );
              if (!currentQuestionnaire?.VenchyEnabled) {
                setIsLoading(false);
              }
            } else {
              setIsLoading(false);
            }
          })
          .catch((err) => {
            console.error(t("ERROR_RECEIVING_AUDIO"), err);
            setIsLoading(false);
          });
      if (!currentQuestion?.NextQuestionId) {
        setIsEnd(true);
      }
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const newValidFiles: File[] = [];
    const newPreviewUrls: string[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const isValidType = allowedTypes.includes(file.type);
      const isValidSize = file.size <= maxFileSizeMB * 1024 * 1024;
      if (isValidType && isValidSize) {
        newValidFiles.push(file);
        newPreviewUrls.push(URL.createObjectURL(file));
      }
    }

    if (newValidFiles.length === 0) {
      console.warn(t("NO_LEGAL_FILES_SELECTED"));
      return;
    }

    setTempFiles((prev) => [...prev, ...newValidFiles]);
    setFilePreviewUrls((prev) => [...prev, ...newPreviewUrls]);
  };

  const handleMultipleFileUpload = () => {
    if (tempFiles.length === 0) {
      return;
    }
    handleAnswer();
    setShowFileUpload(false);
    const date = formatDateTimeInIsrael(new Date());
    setMessages({
      messageId: `${currentQuestionId}${date}`,
      id: user.id,
      date: date,
      unread: false,
      message: t("DOCUMENTS_SENT_SUCCESSFULLY"),
      nextQuestionId: currentQuestion?.NextQuestionId,
      questionId: currentQuestionId,
    });
    setIsLoading(true);
    const newAnswers = tempFiles.map((file) => ({
      file,
      questionId: currentQuestion?.Id || -1,
    }));

    setFileAnswers([...allFileAnswers, ...newAnswers]);

    // clear
    setTempFiles([]);
    setFilePreviewUrls([]);
    if (fileInputRef.current) fileInputRef.current.value = "";

    delay(1500).then(() => {
      setIsLoading(false);
      const nextQuestion =
        allQuestions &&
        currentQuestion?.NextQuestionId &&
        findNextUnansweredQuestion(
          allQuestions,
          currentQuestion.NextQuestionId
        );
      currentQuestion?.NextQuestionId &&
        currentQuestionnaire?.Id &&
        getQuestionById({
          nextQuestionId: nextQuestion
            ? nextQuestion.Id
            : currentQuestion.NextQuestionId,
          skip: false,
          questionnaireId: currentQuestionnaire.Id,
        })
          .then((res) => {
            if ("data" in res && res.data && res.data.Questions) {
              const question = res.data.Questions;
              setIsLoading(true);
              getAudio([{ id: question.Id, desc: question.Desc }]).finally(
                () => {
                  setIsLoading(false);
                }
              );
              if (!currentQuestionnaire?.VenchyEnabled) {
                setIsLoading(false);
              }
            } else {
              setIsLoading(false);
            }
          })
          .catch((err) => {
            console.error(t("ERROR_RECEIVING_AUDIO"), err);
            setIsLoading(false);
          });
      if (!currentQuestion?.NextQuestionId) {
        setIsEnd(true);
      }
    });
  };
  // Show loader only while actively loading
  if (
    isQuestionnaireLoading ||
    isQuestionnaireGuidLoading ||
    isQuestionnaireComponentLoading
  ) {
    return <Loader />;
  }

  // A questionnaire tied to one session is answered once. The link stays valid for ever in the
  // patient's messages, so say plainly that it is spent rather than showing "not found", which
  // reads as something being broken.
  if (!currentQuestionnaire && loadError === "ALREADY_ANSWERED") {
    return (
      <div className={prevencherChatStyles.section}>
        <div className={prevencherChatStyles.errorContainer}>
          <span className={prevencherChatStyles.errorIcon}>✅</span>
          <p className={prevencherChatStyles.errorMessage}>
            {t("QUESTIONNAIRE_ALREADY_ANSWERED")}
          </p>
        </div>
      </div>
    );
  }

  // Show error if loading is complete but no questionnaire was found
  if (!currentQuestionnaire) {
    return (
      <div className={prevencherChatStyles.section}>
        <div className={prevencherChatStyles.errorContainer}>
          <span className={prevencherChatStyles.errorIcon}>😕</span>
          <p className={prevencherChatStyles.errorMessage}>
            {t("NO_SUITABLE_QUESTIONNAIRE")}
          </p>
        </div>
      </div>
    );
  }


  const handleEditAnswer = (questionIdToEdit: number) => {
    if (currentQuestionnaire) {
      setIsLoading(true);

      setShowInput(false);
      setShowTextArea(false);
      setShowReplyBarForStructured(false);

      getQuestionById({
        nextQuestionId: questionIdToEdit,
        skip: false,
        questionnaireId: currentQuestionnaire.Id,
      })
        .then((res) => {
          if ("data" in res && res.data && res.data.Questions) {
            const question = res.data.Questions;
            setIsLoading(true);
            getAudio([{ id: question.Id, desc: question.Desc }]).finally(() => {
              setIsLoading(false);
            });
            if (!currentQuestionnaire?.VenchyEnabled) {
              setIsLoading(false);
            }
          } else {
            setIsLoading(false);
          }
        })
        .catch((err) => {
          console.error(t("ERROR_RECEIVING_AUDIO"), err);
          setIsLoading(false);
        });
    }
  };

  return (
    <>
      <div>
        <ProgressBar
          answeredCount={answeredCount}
          totalQuestions={totalQuestions}
        />
      </div>
      {currentQuestionnaire.VenchyEnabled && (
        <VenchySpeaking audioUrl={lastMessageAudio || currentAudio?.audioUrl} />
        // <VenchySpeaking audioUrl={
        //   messages[messages.length - 1]?.audio
        //     ? messages[messages.length - 1].audio
        //     : (audioUrls[0] && currentQuestionId === audioUrls[0].questionId
        //       ? audioUrls[0].audioUrl
        //       : undefined)
        // } />
      )}

      <div
        dir={i18n.dir()}
        data-testid="chat-panel"
        className={`
          ${prevencherChatStyles.section} ${prevencherChatStyles.section_POC}
          ${
            currentQuestionnaire?.VenchyEnabled &&
            prevencherChatStyles.section_POC_Venchy
          }
        `}
      >
        <div className={prevencherChatStyles.scrollableMessages}>
          {phoneExistsPopup && pendingPhoneData ? (
            <Modal
              onClose={
                window.location.hostname.includes("test") ||
                window.location.hostname.includes("local")
                  ? () => setPhoneExistsPopup(false)
                  : () => {}
              }
            >
              <PhoneExistsPopup
                onConfirmNewPhone={handleNewPhoneSubmit}
                onContinueWithExisting={handleContinueWithExistingPhone}
              />
            </Modal>
          ) : isUserExist && userExistPopup ? (
            <Modal
              onClose={
                window.location.hostname.includes("test") ||
                window.location.hostname.includes("local")
                  ? () => setUserExistPopup(false)
                  : () => {}
              }
            >
              <UserExistPopup
                onClose={handleContinueQuestionnaireAfterUserExist}
                guid={UserGuid}
              />
            </Modal>
          ) : passwordVerifecationPopup &&
            verificationData &&
            currentPhone &&
            currentQuestionnaire ? (
            <Modal onClose={() => {}}>
              <PasswordVerificationPopup
                verificationData={verificationData}
                companyId={currentQuestionnaire.CompanyId}
                phone={currentPhone}
                onClose={() => {
                  setTimeout(() => {
                    setPasswordVerifecationPopup(false);
                  }, 1000);
                  delay(1500).then(() => {
                    currentQuestionnaire?.Id &&
                      setAnswers({
                        Id: "0",
                        QuestionnaireId: currentQuestionnaire.Id.toString(),
                        QuestionsId: currentQuestionId.toString(),
                        UsersId: usersId ?? "0",
                        Answer: currentPhone,
                      });
                    setIsLoading(false);
                    setInputValue("");
                    if (
                      currentQuestion?.NextQuestionId &&
                      currentQuestionnaire
                    ) {
                      getQuestionById({
                        nextQuestionId: currentQuestion.NextQuestionId,

                        skip: false,
                        questionnaireId: currentQuestionnaire.Id,
                      })
                        .then((res) => {
                          if ("data" in res && res.data && res.data.Questions) {
                            const question = res.data.Questions;
                            setIsLoading(true);
                            currentQuestionnaire?.VenchyEnabled &&
                              getAudio([
                                { id: question.Id, desc: question.Desc },
                              ]).finally(() => {
                                setIsLoading(false);
                              });
                            if (!currentQuestionnaire?.VenchyEnabled) {
                              setIsLoading(false);
                            }
                          } else {
                            setIsLoading(false);
                          }
                        })
                        .catch((err) => {
                          console.error(t("ERROR_RECEIVING_AUDIO"), err);
                          setIsLoading(false);
                        });
                    } else {
                      setIsEnd(true);
                    }
                    //.finally(() => { setIsLoading(false); });
                    if (!currentQuestion?.NextQuestionId) {
                      setIsEnd(true);
                    }
                  });
                }}
              />
            </Modal>
          ) : (
            messages.length > 0 &&
            messages.map((message) => (
              <Message
                key={message.messageId}
                isOwnMessage={user.id === message.id}
                message={message}
                onEditClick={handleEditAnswer}
                isLoading={
                  isLoading ||
                  !!(
                    currentQuestion &&
                    !currentQuestion.NextQuestionId &&
                    currentQuestion.DataTypesId !== 1 &&
                    currentQuestionnaire?.CompanyId
                  ) ||
                  isEnd
                }
              />
            ))
          )}
          {options.length > 0 && (
            <div className={prevencherChatStyles.multiSelectContainer}>
              {isMultiSelect && (
                <div className={prevencherChatStyles.selectionCounter}>
                  {t("SELECTED_OPTIONS", {
                    selected: selectedOptions.length,
                    min: currentQuestion?.OptionsCountMin ?? 0,
                    max: currentQuestion?.OptionsCountMax ?? 1,
                  })}
                </div>
              )}
              <ul
                className={`${prevencherChatStyles.variants} ${prevencherChatStyles.variants_poc} `}
              >
                {options.map((o, index) => (
                  <li
                    data-testid={`chat-option-${index}`}
                    className={`${prevencherChatStyles.variant} ${
                      selectedOptions.includes(o.Id)
                        ? prevencherChatStyles.variant_selected
                        : ""
                    } hover`}
                    key={o.Id}
                    onClick={() => {
                      handleOptionToggle(o);
                      if (!isMultiSelect) {
                        handleAnswer();
                      }
                    }}
                  >
                    <span>{o.Desc}</span>
                  </li>
                ))}
              </ul>
              {isMultiSelect && (
                <>
                  <div className={prevencherChatStyles.multiSelectActions}>
                    <button
                      type="button"
                      className={prevencherChatStyles.multiSelectSendBtn}
                      onClick={handleMultiSelectSubmit}
                      disabled={
                        !canSkipQuestion &&
                        selectedOptions.length < (currentQuestion?.OptionsCountMin ?? 0)
                      }
                    />
                    {canSkipQuestion && (
                      <div className={prevencherChatStyles.skipButtonContainer}>
                        <button
                          type="button"
                          className={prevencherChatStyles.skipButton}
                          onClick={handleSkipQuestion}
                          title={t("SKIP_QUESTION")}
                        >
                          <span className={prevencherChatStyles.skipButtonText}>
                            {t("SKIP")}
                          </span>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={2.5}
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"
                            />
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>
                  {validationError && (
                    <div className={prevencherChatStyles.validationError}>
                      {validationError}
                    </div>
                  )}
                </>
              )}
              {!isMultiSelect && canSkipQuestion && (
                <div className={prevencherChatStyles.skipButtonContainer} style={{ marginTop: '8px' }}>
                  <button
                    type="button"
                    className={prevencherChatStyles.skipButton}
                    onClick={handleSkipQuestion}
                    title={t("SKIP_QUESTION")}
                  >
                    <span className={prevencherChatStyles.skipButtonText}>
                      {t("SKIP")}
                    </span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={2.5}
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"
                      />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          )}
          <div
            className={`loader ${
              (isLoading ||
                isQuestionnaireLoading ||
                isQuestionnaireGuidLoading ||
                isQuestionnaireComponentLoading ||

                isAiResponding) &&
              "loader_visible"
            } `}
          >
            <Loader />
            <p className={prevencherChatStyles.typing}>{t("VENCHI_TYPING")}</p>
          </div>
          <div ref={lastElRef} />
        </div>

        <div>
          {isAIPhase || showReplyBarForStructured ? (
            <div className={canSkipQuestion && showReplyBarForStructured ? prevencherChatStyles.inputWithSkip : ''}>
              <ReplyBar
                ref={replyBarRef}
                isLoading={isAiResponding || isEnd}
                isFinished={isEnd}
                onSendMessage={handleTextSendWrapper}
                onSendVoiceMessage={handleAISendVoiceMessage}
                styles={{
                  replyBar: prevencherChatStyles.replyBar,
                  replyBarAudio: prevencherChatStyles.replyBarAudio,
                  message: prevencherChatStyles.message || "",
                  textArea: prevencherChatStyles.textArea || "",
                  btn: prevencherChatStyles.btn || "",
                  sendBtn: prevencherChatStyles.sendBtn,
                }}
              />
              {canSkipQuestion && showReplyBarForStructured && (
                <div className={prevencherChatStyles.skipButtonContainer}>
                  <button
                    type="button"
                    className={prevencherChatStyles.skipButton}
                    onClick={handleSkipQuestion}
                    title={t("SKIP_QUESTION")}
                  >
                    <span className={prevencherChatStyles.skipButtonText}>
                      {t("SKIP")}
                    </span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={2.5}
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"
                      />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              {showInput && (
                <div className={canSkipQuestion ? prevencherChatStyles.inputWithSkip : ''}>
                  <div
                    className={`${prevencherChatStyles.inputContainer} ${prevencherChatStyles.inputContainer_position_left} `}
                  >
                    <input
                      ref={textAreaRef as any}
                      type={
                        currentQuestion?.DataTypesId === 4
                          ? "date"
                          : currentQuestion?.DataTypesId === 3
                          ? "number"
                          : "text"
                      }
                      name={"input"}
                      autoFocus={true}
                      required={true}
                      data-testid={
                        currentQuestion?.DataTypesId === 4
                          ? "date-input"
                          : currentQuestion?.DataTypesId === 3
                          ? "phone-input"
                          : "chat-text-input"
                      }
                      aria-label={
                        currentQuestion?.DataTypesId === 3
                          ? "phone-input"
                          : undefined
                      }
                      className={`${prevencherChatStyles.variant} ${prevencherChatStyles.input} `}
                      value={inputValue}
                      onChange={
                        currentQuestion?.DataTypesId === 3
                          ? handleTelInputChange
                          : handleInputChange
                      }
                      onKeyDown={(event) => handleKeyDown(event)}
                      onClick={() => {}}
                      maxLength={
                        currentQuestion?.UserTableField === "FirstName"
                          ? 20
                          : currentQuestion?.DataTypesId === 5
                          ? 50
                          : 300
                      }
                    />
                    <button
                      type={"button"}
                      className={prevencherChatStyles.sendBtn}
                      onClick={() => onInputSubmit(inputValue)}
                    />
                  </div>
                  {canSkipQuestion && (
                    <div className={prevencherChatStyles.skipButtonContainer}>
                      <button
                        type="button"
                        className={prevencherChatStyles.skipButton}
                        onClick={handleSkipQuestion}
                        title={t("SKIP_QUESTION")}
                      >
                        <span className={prevencherChatStyles.skipButtonText}>
                          {t("SKIP")}
                        </span>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={2.5}
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"
                          />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              )}
              {showTextArea && (
                <div className={canSkipQuestion ? prevencherChatStyles.inputWithSkip : ''}>
                  <div
                    className={`${prevencherChatStyles.inputContainer} ${prevencherChatStyles.inputContainer_position_left} `}
                  >
                    <textarea
                      autoFocus={true}
                      required={true}
                      className={`${prevencherChatStyles.variant}  ${prevencherChatStyles.input} `}
                      value={inputValue}
                      onChange={handleInputChange}
                      onKeyDown={(event) => handleKeyDown(event)}
                    />
                    <button
                      type={"button"}
                      className={prevencherChatStyles.sendBtn}
                      onClick={() => onInputSubmit(inputValue)}
                    />
                  </div>
                  {canSkipQuestion && (
                    <div className={prevencherChatStyles.skipButtonContainer}>
                      <button
                        type="button"
                        className={prevencherChatStyles.skipButton}
                        onClick={handleSkipQuestion}
                        title={t("SKIP_QUESTION")}
                      >
                        <span className={prevencherChatStyles.skipButtonText}>
                          {t("SKIP")}
                        </span>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={2.5}
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"
                          />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              )}
              {showVoiceMessageButton && (
                <div className={canSkipQuestion ? prevencherChatStyles.inputWithSkip : ''}>
                  <div className={prevencherChatStyles.replyBar}>
                    <VoiceMessageButton
                      isRecording={isRecording}
                      audioURL={audioURL}
                      onStart={startRecording}
                      onStop={stopRecording}
                      onDelete={deleteRecord}
                      onSend={async () => {
                        if (audioURL) {
                          const response = await fetch(audioURL);
                          const blob = await response.blob();
                          saveVoiceMessage(blob);
                          deleteRecord();
                        }
                      }}
                      disabled={isLoading}
                    />
                  </div>
                  {canSkipQuestion && (
                    <div className={prevencherChatStyles.skipButtonContainer}>
                      <button
                        type="button"
                        className={prevencherChatStyles.skipButton}
                        onClick={handleSkipQuestion}
                        title={t("SKIP_QUESTION")}
                      >
                        <span className={prevencherChatStyles.skipButtonText}>
                          {t("SKIP")}
                        </span>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={2.5}
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"
                          />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              )}

              {showFileUpload && (
                <div className={canSkipQuestion ? prevencherChatStyles.inputWithSkip : ''}>
                  <div className={prevencherChatStyles.uploadWrapper}>
                    <FileUpload
                      onFileChange={handleFileChange}
                      onSendClick={handleMultipleFileUpload}
                      showSendButton={tempFiles.length > 0}
                    />
                    {filePreviewUrls.length > 0 && (
                      <div className={prevencherChatStyles.filePreviewGrid}>
                        {filePreviewUrls.map((url, index) => (
                          <FilePreview
                            key={index}
                            file={tempFiles[index]}
                            previewUrl={url}
                            onRemove={() => {
                              setTempFiles((prev) =>
                                prev.filter((_, i) => i !== index)
                              );
                              setFilePreviewUrls((prev) =>
                                prev.filter((_, i) => i !== index)
                              );
                            }}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                  {canSkipQuestion && (
                    <div className={prevencherChatStyles.skipButtonContainer}>
                      <button
                        type="button"
                        className={prevencherChatStyles.skipButton}
                        onClick={handleSkipQuestion}
                        title={t("SKIP_QUESTION")}
                      >
                        <span className={prevencherChatStyles.skipButtonText}>
                          {t("SKIP")}
                        </span>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={2.5}
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"
                          />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              )}

              {showSignature && (
                <SignaturePad onConfirm={onSignatureSubmit} disabled={isLoading} />
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default Chat;
